import React, { useState, useEffect, useRef } from "react";
import { Button, Input } from "antd";
import {
  FiPhone,
  FiArrowLeft,
  FiMail,
  FiMapPin,
  FiUser,
  FiShield,
} from "react-icons/fi";
import { useFormik } from "formik";
import { useSelector } from "react-redux";
import { registerSchema } from "../../../validations/authValidation";

const RegisterStep = ({
  mobile,
  onSubmit,
  onResend,
  onBackToOtp,
  onUpdateMobile,
  loading,
  isDarkMode,
}) => {
  const [timer, setTimer] = useState(59);
  const [canResend, setCanResend] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const inputRefs = useRef([]);
  const { selectedCity } = useSelector((state) => state.city);

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      pincode: "",
      otp: "",
      otpToken: "",
      city_id: selectedCity?.id || "",
    },
    validationSchema: registerSchema,
    onSubmit: async (values) => {
      setOtpError(null);
      const result = await onSubmit({
        ...values,
        mobile,
        city_id: selectedCity?.id || values.city_id,
      });
      if (result?.error) setOtpError(result.error);
    },
  });

  useEffect(() => {
    if (timer === 0) {
      setCanResend(true);
      return;
    }
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const handleResend = async () => {
    if (!canResend) return;
    try {
      await onResend();
      setTimer(59);
      setCanResend(false);
      setOtpError(null);
    } catch (error) {
      setOtpError("Failed to resend OTP. Please try again.");
    }
  };

  const handleOtpChange = (value) => {
    const numericValue = value.replace(/\D/g, "").slice(0, 6);
    formik.setFieldValue("otp", numericValue);
    if (otpError) setOtpError(null);
  };

  const handleOtpInputChange = (index, value) => {
    const newOtp = (formik.values.otp || "").split("");
    newOtp[index] = value.replace(/\D/g, "");
    handleOtpChange(newOtp.join(""));

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !formik.values.otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const getError = (field) => formik.touched[field] && formik.errors[field];

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            Registering for
          </p>
          <p
            className={`text-sm font-medium flex items-center gap-1 ${isDarkMode ? "text-white" : "text-gray-800"}`}
          >
            <FiPhone size={14} className="text-green-600" /> +91 {mobile}
          </p>
        </div>
        <button
          type="button"
          onClick={onUpdateMobile}
          className="text-xs font-medium text-green-600 hover:text-green-700"
        >
          Change
        </button>
      </div>

      <div>
        <label
          className={`text-xs font-medium mb-1 block ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          Full Name <span className="text-red-500">*</span>
        </label>
        <Input
          size="large"
          name="name"
          placeholder="Enter your full name"
          prefix={<FiUser className="text-gray-400" />}
          value={formik.values.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          status={getError("name") ? "error" : ""}
          className={`w-full ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
        />
        {getError("name") && (
          <div className="mt-1 text-xs text-red-500">{getError("name")}</div>
        )}
      </div>

      <div>
        <label
          className={`text-xs font-medium mb-1 block ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          Email Address <span className="text-red-500">*</span>
        </label>
        <Input
          size="large"
          type="email"
          name="email"
          placeholder="Enter your email"
          prefix={<FiMail className="text-gray-400" />}
          value={formik.values.email}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          status={getError("email") ? "error" : ""}
          className={`w-full ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
        />
        {getError("email") && (
          <div className="mt-1 text-xs text-red-500">{getError("email")}</div>
        )}
      </div>

      <div>
        <label
          className={`text-xs font-medium mb-1 block ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          Pincode <span className="text-red-500">*</span>
        </label>
        <Input
          size="large"
          name="pincode"
          placeholder="Enter 6-digit pincode"
          prefix={<FiMapPin className="text-gray-400" />}
          value={formik.values.pincode}
          onChange={(e) =>
            formik.setFieldValue(
              "pincode",
              e.target.value.replace(/\D/g, "").slice(0, 6),
            )
          }
          onBlur={formik.handleBlur}
          maxLength={6}
          status={getError("pincode") ? "error" : ""}
          className={`w-full ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
        />
        {getError("pincode") && (
          <div className="mt-1 text-xs text-red-500">{getError("pincode")}</div>
        )}
      </div>

      <div>
        <label
          className={`text-xs font-medium mb-2 block ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}
        >
          Enter OTP (sent to {mobile})
        </label>
        <div className="flex justify-between gap-2">
          {[...Array(6)].map((_, index) => (
            <Input
              key={index}
              ref={(el) => (inputRefs.current[index] = el)}
              value={(formik.values.otp || "")[index] || ""}
              onChange={(e) => handleOtpInputChange(index, e.target.value)}
              onKeyDown={(e) => handleOtpKeyDown(index, e)}
              id={`register-otp-${index}`}
              maxLength={1}
              className={`w-10 h-10 text-center text-lg font-bold ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : ""}`}
            />
          ))}
        </div>
        {otpError && (
          <div className="mt-1 text-xs text-red-500">{otpError}</div>
        )}
        {getError("otp") && !otpError && (
          <div className="mt-1 text-xs text-red-500">{getError("otp")}</div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handleResend}
          disabled={!canResend}
          className={`text-xs font-medium ${
            canResend
              ? "text-green-600 hover:text-green-700"
              : isDarkMode
                ? "text-gray-500 cursor-not-allowed"
                : "text-gray-400 cursor-not-allowed"
          }`}
        >
          Resend OTP
        </button>
        {!canResend && (
          <span
            className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            in 00:{timer.toString().padStart(2, "0")}s
          </span>
        )}
      </div>

      <button
        type="button"
        onClick={onBackToOtp}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
      >
        <FiArrowLeft size={12} /> Back to OTP verification
      </button>

      <Button
        type="primary"
        size="large"
        block
        htmlType="submit"
        loading={loading}
        className="mt-2 bg-green-600 hover:bg-green-700"
      >
        Register & Login
      </Button>

      <div
        className={`p-3 rounded-lg flex items-start gap-2 ${isDarkMode ? "bg-gray-800" : "bg-green-50"}`}
      >
        <FiShield
          className={`mt-0.5 ${isDarkMode ? "text-green-400" : "text-green-600"}`}
          size={16}
        />
        <p
          className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
        >
          Your data is end-to-end encrypted and 100% safe
        </p>
      </div>
    </form>
  );
};

export default RegisterStep;
